import { pregnancyStickerUrl } from "@listou/shared/stickers";

import { babyWeek, comparison, formatLength, formatWeight } from "../../lib/baby-weeks";
import { formatDay } from "../../lib/format";
import type { Dating } from "../../lib/pregnancy";
import { Sticker } from "../list-page/parts";

const TERM_SA = 41;
const TRIMESTERS = [
  { label: "1er trimestre", from: 0, to: 15, className: "bg-[#fbd5df]" },
  { label: "2e trimestre", from: 15, to: 28, className: "bg-[#fde7b0]" },
  { label: "3e trimestre", from: 28, to: TERM_SA, className: "bg-[#cfe3f4]" },
];

const percent = (sa: number) => `${(Math.min(sa, TERM_SA) / TERM_SA) * 100}%`;

/**
 * The pregnancy as one bar from 0 to 41 SA: the trimesters, the three scans, and a pin
 * for today. Shows where one is at a glance, which a list of dates does not.
 */
export function PregnancyTimeline({
  result,
  todaySa,
}: {
  result: Dating;
  /** Days since the period-equivalent start, or null outside the pregnancy. */
  todaySa: number | null;
}) {
  const scanWeeks = [11, 20, 30];
  return (
    <figure className="grid gap-3">
      <div className="relative pt-9 pb-7">
        {todaySa !== null && (
          <div
            className="absolute top-0 z-10 grid -translate-x-1/2 justify-items-center"
            style={{ left: percent(todaySa / 7) }}
          >
            <span className="rounded-full bg-ink px-2.5 py-0.5 text-xs font-bold whitespace-nowrap text-surface">
              Aujourd'hui
            </span>
            <span className="h-3 w-0.5 bg-ink" />
          </div>
        )}
        <div className="flex h-5 overflow-hidden rounded-full">
          {TRIMESTERS.map((trimester) => (
            <div
              key={trimester.label}
              className={trimester.className}
              style={{ width: `${((trimester.to - trimester.from) / TERM_SA) * 100}%` }}
            />
          ))}
        </div>
        {todaySa !== null && (
          <div
            className="absolute top-9 left-0 h-5 rounded-full bg-accent/35"
            style={{ width: percent(todaySa / 7) }}
          />
        )}
        {scanWeeks.map((weeks, index) => (
          <span
            key={weeks}
            className="absolute bottom-0 -translate-x-1/2 text-xs font-bold text-muted"
            style={{ left: percent(weeks + 1) }}
            title={result.scans[index]?.label}
          >
            Écho {index + 1}
          </span>
        ))}
      </div>
      <figcaption className="flex flex-wrap justify-between gap-2 text-sm text-muted">
        {TRIMESTERS.map((trimester) => (
          <span key={trimester.label} className="flex items-center gap-1.5">
            <span className={`size-3 rounded-full ${trimester.className}`} />
            {trimester.label}
          </span>
        ))}
        <span>Terme : {formatDay(result.dueDate)}</span>
      </figcaption>
    </figure>
  );
}

/** "Cette semaine, bébé a la taille d'une framboise": the card everyone screenshots. */
export function BabyThisWeek({ weeks, title }: { weeks: number; title?: string }) {
  const entry = babyWeek(weeks);
  if (!entry) return null;
  return (
    <div className="grid items-center gap-5 rounded-[1.75rem] bg-[#fdf3e1] p-5 sm:grid-cols-[auto_1fr] sm:p-6">
      <Sticker
        src={pregnancyStickerUrl(entry.sticker)}
        className="mx-auto w-32 -rotate-6 drop-shadow-[0_6px_10px_rgba(31,37,68,0.18)] sm:w-36"
      />
      <div className="grid gap-2">
        <p className="text-sm font-bold text-accent-text">{title ?? `À ${entry.sa} SA`}</p>
        <p className="font-soft text-3xl leading-tight font-medium">Bébé {comparison(entry)}</p>
        <p className="flex flex-wrap gap-x-4 font-bold">
          <span>{formatLength(entry.lengthCm)}</span>
          <span>{formatWeight(entry.weightG)}</span>
        </p>
        <p className="text-ink/80">{entry.note}</p>
      </div>
    </div>
  );
}
